import { memo, type HTMLAttributes, type AnchorHTMLAttributes, type ImgHTMLAttributes } from 'react';

// Shared markdown components with consistent Tailwind styling
const MarkdownULComponent = ({ children }: HTMLAttributes<HTMLElement>) => (
    <ul className="my-5 ms-5 list-disc space-y-2">{children}</ul>
);
export const MarkdownUL = memo(MarkdownULComponent) as typeof MarkdownULComponent;

const MarkdownOLComponent = ({ children }: HTMLAttributes<HTMLElement>) => (
    <ol className="my-5 ms-5 list-decimal space-y-2">{children}</ol>
);
export const MarkdownOL = memo(MarkdownOLComponent) as typeof MarkdownOLComponent;

const MarkdownStrongComponent = ({ children }: HTMLAttributes<HTMLElement>) => (
    <strong className="font-semibold">{children}</strong>
);
export const MarkdownStrong = memo(MarkdownStrongComponent) as typeof MarkdownStrongComponent;

const MarkdownEmComponent = ({ children }: HTMLAttributes<HTMLElement>) => <em className="italic">{children}</em>;
export const MarkdownEm = memo(MarkdownEmComponent) as typeof MarkdownEmComponent;

const MarkdownLIComponent = ({ children }: HTMLAttributes<HTMLElement>) => (
    <li className="[&_ol]:my-2 [&_ul]:my-2">{children}</li>
);
export const MarkdownLI = memo(MarkdownLIComponent) as typeof MarkdownLIComponent;

const MarkdownCodeComponent = ({ children }: HTMLAttributes<HTMLElement>) => (
    <code className="rounded bg-gray-100 px-1 py-0.5 font-mono text-sm">{children}</code>
);
export const MarkdownCode = memo(MarkdownCodeComponent) as typeof MarkdownCodeComponent;

const MarkdownPreComponent = ({ children }: HTMLAttributes<HTMLElement>) => (
    <pre className="overflow-x-auto rounded bg-gray-100 p-3 [&_code]:bg-transparent [&_code]:p-0">{children}</pre>
);
export const MarkdownPre = memo(MarkdownPreComponent) as typeof MarkdownPreComponent;

const MarkdownBlockquoteComponent = ({ children }: HTMLAttributes<HTMLElement>) => (
    <blockquote className="my-4 border-l-4 border-gray-300 pl-4 italic">{children}</blockquote>
);
export const MarkdownBlockquote = memo(MarkdownBlockquoteComponent) as typeof MarkdownBlockquoteComponent;

const MarkdownTableComponent = ({ children }: HTMLAttributes<HTMLElement>) => (
    <table className="my-4 w-full border-collapse">{children}</table>
);
export const MarkdownTable = memo(MarkdownTableComponent) as typeof MarkdownTableComponent;

const MarkdownThComponent = ({ children }: HTMLAttributes<HTMLElement>) => (
    <th className="border border-gray-300 bg-gray-50 px-3 py-2 text-start font-semibold">{children}</th>
);
export const MarkdownTh = memo(MarkdownThComponent) as typeof MarkdownThComponent;

const MarkdownTdComponent = ({ children }: HTMLAttributes<HTMLElement>) => (
    <td className="border border-gray-300 px-3 py-2 text-start">{children}</td>
);
export const MarkdownTd = memo(MarkdownTdComponent) as typeof MarkdownTdComponent;

const MarkdownHrComponent = ({ ...rest }: HTMLAttributes<HTMLElement>) => (
    <hr {...rest} className="my-6 border-gray-300" />
);
export const MarkdownHr = memo(MarkdownHrComponent) as typeof MarkdownHrComponent;

// Header components
const MarkdownH1Component = ({ children }: HTMLAttributes<HTMLElement>) => (
    <h1 className="mb-[10px] text-xl font-normal text-black">{children}</h1>
);
export const MarkdownH1 = memo(MarkdownH1Component) as typeof MarkdownH1Component;

const MarkdownH2Component = ({ children }: HTMLAttributes<HTMLElement>) => (
    <h2 className="mb-[10px] text-lg font-normal text-black">{children}</h2>
);
export const MarkdownH2 = memo(MarkdownH2Component) as typeof MarkdownH2Component;

const MarkdownH3Component = ({ children }: HTMLAttributes<HTMLElement>) => (
    <h3 className="mb-[10px] text-base font-normal text-black">{children}</h3>
);
export const MarkdownH3 = memo(MarkdownH3Component) as typeof MarkdownH3Component;

const MarkdownH4Component = ({ children }: HTMLAttributes<HTMLElement>) => (
    <h4 className="mb-[10px] text-sm font-normal text-black">{children}</h4>
);
export const MarkdownH4 = memo(MarkdownH4Component) as typeof MarkdownH4Component;

const MarkdownH5Component = ({ children }: HTMLAttributes<HTMLElement>) => (
    <h5 className="mb-[10px] text-sm font-normal text-black">{children}</h5>
);
export const MarkdownH5 = memo(MarkdownH5Component) as typeof MarkdownH5Component;

const MarkdownH6Component = ({ children }: HTMLAttributes<HTMLElement>) => (
    <h6 className="mb-[10px] text-sm font-normal text-black">{children}</h6>
);
export const MarkdownH6 = memo(MarkdownH6Component) as typeof MarkdownH6Component;

// Paragraph component
const MarkdownParagraphComponent = ({ children }: HTMLAttributes<HTMLElement>) => (
    <p className="mb-3 last:mb-0">{children}</p>
);
export const MarkdownParagraph = memo(MarkdownParagraphComponent) as typeof MarkdownParagraphComponent;

// Basic link component - can be customized per use case
const MarkdownLinkComponent = ({ children, ...rest }: AnchorHTMLAttributes<HTMLAnchorElement>) => (
    <a {...rest} className="text-blue-600 underline hover:text-blue-800">
        {children}
    </a>
);
export const MarkdownLink = memo(MarkdownLinkComponent) as typeof MarkdownLinkComponent;

// Basic image component
const MarkdownImageComponent = ({ alt, ...rest }: ImgHTMLAttributes<HTMLImageElement>) => <img {...rest} alt={alt} />;
export const MarkdownImage = memo(MarkdownImageComponent) as typeof MarkdownImageComponent;
